import React from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ShootPlanner from "../components/site/ShootPlanner";
import {
  MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";

export default function MenuShootPlanner() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <Checklist />
      <ShootPlanner />
      <DayOfShoot />
      <MelbourneSection
        serviceLabel="Menu Shoot Planning"
        eyebrow="Coverage · All Melbourne metro"
        headline="A planning service for"
        italic="every Melbourne kitchen."
        intro="The planner above scopes the shoot day in detail. We coordinate timing, dish order, and prop kit to fit around your service — most shoots run pre-doors or in a quiet mid-service window."
      />
      <SisterServices keys={["menu", "food", "venue", "brand"]} />
      <FAQSection
        eyebrow="Common questions · Menu shoot planning"
        headline="Prep, scope,"
        italic="and shoot-day flow."
        intro="Most of the planning is short — a 15-minute call to confirm the brief is usually enough."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to plan"
        italic="your shoot?"
        intro="Use the planner above, or call directly. We'll lock the date, brief and prop list the same business day."
        backdropSlug="hero-spread-01"
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[80svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="hero-banquet-01" alt="Menu shoot planner Melbourne" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/45 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[80svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Menu Shoot Planner" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Plan it properly · Free interactive planner
        </p>
        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
          Plan a menu shoot
          <br />
          like <span className="italic font-light text-ember">a pro.</span>
        </h1>
        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Use the planner to map out dish count, add-ons and shoot day — then
          send it through. We&apos;ll review and confirm the brief with you on
          a 15-minute call.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#planner"><CTAButton variant="primary" size="lg">Open The Planner</CTAButton></a>
        </div>
      </div>
    </div>
  </section>
);

const STEPS = [
  { n: "01", t: "Decide the menu scope", d: "Pick which dishes need a photo. For a 14-dish Brunswick brunch venue, that's usually the 6 hero plates plus 4 supporting boards. For a 60-dish Carlton group venue, scope the 25 best-sellers first — the long tail can be added in a second shoot. Skip anything you're retiring." },
  { n: "02", t: "Choose the channels", d: "Web (16:9), Google Business (1080×608 cover + 10 dishes), Uber Eats (1:1 square 968px min), Instagram feed (4:5) and Stories/Reels (9:16) — each crop is captured in-frame so no recropping later. The planner ticks all of them in one shoot day." },
  { n: "03", t: "Lock the date", d: "Tuesday to Thursday between 7:30–11am is the Melbourne sweet spot — kitchen prep is calmer, espresso is dialled in by 8am, traffic into the inner-north and bayside is light. Friday/Saturday possible but the prep team is under service pressure. Avoid AFL Grand Final week." },
  { n: "04", t: "Prep the kitchen", d: "Fresh garnish (especially herbs — coriander wilts in 20 min under lights), clean plates, branded crockery if relevant, sauces in squeeze bottles, A-shift chef on the pass. We bring everything else — surfaces, linens, lighting, glassware, props, colour-calibrated monitor." },
];

const Checklist = () => (
  <section className="relative bg-ink text-bone py-20 lg:py-28 overflow-hidden">
    <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-12 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">The 4-step shoot plan</p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
            From idea
            <br />
            <span className="italic font-light text-ember">to locked date.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            Use the steps below before opening the planner. Most shoots come
            together in under 24 hours from first call to locked date.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {STEPS.map((s) => (
          <div key={s.n} className="bg-ink p-7 md:p-8">
            <span className="font-display italic text-2xl text-ember leading-none">{s.n}</span>
            <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-3">{s.t}</h3>
            <p className="mt-4 text-sm text-bone/75 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DayOfShoot = () => (
  <section className="bg-bone text-ink py-20 lg:py-28">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">Day-of checklist</p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
            What we bring.
            <br />
            <span className="italic font-light text-ember">What you bring.</span>
          </h2>
          <p className="mt-6 text-char text-base md:text-lg leading-relaxed">
            We arrive with everything except the food. You arrive with a kitchen
            ready to plate. Most prep is invisible to your guests — most shoots
            wrap before the room opens.
          </p>
        </div>
        <div className="col-span-12 lg:col-span-7 grid sm:grid-cols-2 gap-8">
          <div>
            <h3 className="font-display text-xl tracking-editorial mb-4">We bring</h3>
            <ul className="space-y-2 text-char">
              {["Full lighting kit", "Surface boards & linens", "Glassware & cutlery props", "Tripod & rigging", "Macro + standard lens kit", "Colour-calibrated monitor", "Backup body & memory"].map(i => (
                <li key={i} className="flex items-start gap-2"><Check size={14} className="text-ember mt-1 shrink-0" /><span className="text-sm md:text-base">{i}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display text-xl tracking-editorial mb-4">You bring</h3>
            <ul className="space-y-2 text-char">
              {["Plated dishes (per the brief)", "Fresh garnish & sauces", "Branded plateware if relevant", "Access to the venue", "A staff member on call", "Final menu list"].map(i => (
                <li key={i} className="flex items-start gap-2"><Check size={14} className="text-ember mt-1 shrink-0" /><span className="text-sm md:text-base">{i}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const FAQS = [
  { q: "How far ahead should I book a menu shoot in Melbourne?", a: "Most venues book 1–3 weeks ahead during the standard February–November window. November to early-December books out 3–4 weeks ahead because every venue is rebuilding their summer menu and Uber Eats library before the Christmas trade. Urgent 3–5 day turnarounds are possible most of the year — call directly and we'll confirm the same business day." },
  { q: "What's the best day of the week to shoot in a working kitchen?", a: "Tuesday to Thursday between 7:30am and 11am is the Melbourne sweet spot — kitchen prep is calmer, the espresso machine is dialled in by 8am, your A-shift chef is on the pass, and traffic into Brunswick / Collingwood / Carlton / South Melbourne is light. Friday/Saturday shoots are possible but your prep team is under service pressure — we typically work pre-doors (7am–10am) or post-service (3–5pm) on those days." },
  { q: "Can I add more dishes after I submit the planner?", a: "Yes — up to 24 hours before the shoot day. The planner total recalculates automatically when you change dish counts. After the shoot day is locked, last-minute additions on-site are quoted at the next-tier per-image rate (e.g. if you're at $25/image, on-day extras come in at $30/image). Most of our clients pre-list a 'maybe' tier of 3–5 dishes we shoot if time permits in the final hour." },
  { q: "Do you provide a shot list before the shoot day?", a: "Yes — we send a numbered shot list 48 hours before the shoot matching your menu items, with a tick-off column for the chef and a target time per dish (typically 8–12 minutes for a complex plate, 3–5 minutes for a side or dessert). Most Melbourne shoots run start-to-finish from that list with one re-edit per dish baked in." },
];
